/*
 * Copyright (c) 2023. Bluemate B.V.
 */
import React from 'react';

import {ChangeFunction} from '@dvrd/dvr-controls';

import {BluemateSelect} from '../../index';

import {COUNTRIES} from '../../../../util/countryUtil';

import {BluemateControlShape} from '../../../../util/types';
import {useTranslation} from 'react-i18next';

interface Props {
    onChange: ChangeFunction;
    value?: string;
    disabled?: boolean;
    className?: string;
    itemsPosition?: 'top' | 'bottom';
    itemsHeight?: number | string;
    labelAsValue?: boolean;
    label?: string;
    labelClassName?: string;
    active?: boolean;
    withAll?: boolean;
    excludeCountryCodes?: string[];
    itemContainerClassName?: string;
    canOpen?: () => boolean;
    shape?: BluemateControlShape;
    /** Forwarded to BluemateSelect: unified 0.9rem/500 label + 1rem/500 value. */
    uniformFont?: boolean;
}

function getUsableCountries(countries: Record<string, string>, excludeCountryCodes?: string[]): Record<string, string> {
    if (!excludeCountryCodes?.length) return countries;
    const usableCountries: Record<string, string> = {};
    for (const countryCode of Object.keys(countries)) {
        if (!excludeCountryCodes.includes(countryCode)) usableCountries[countryCode] = countries[countryCode];
    }
    return usableCountries;
}

const languageKey = 'controls.countryField';

export default function CountryField(props: Props) {
    const {
        className,
        disabled,
        itemsPosition,
        onChange,
        value,
        labelAsValue,
        label,
        labelClassName,
        active,
        withAll,
        excludeCountryCodes,
        itemContainerClassName,
        canOpen,
        itemsHeight,
        shape,
        uniformFont
    } = props;
    const {t} = useTranslation();
    const usableCountries = getUsableCountries(COUNTRIES, excludeCountryCodes);
    const items = Object.keys(usableCountries).map((countryCode: string) => ({
        value: labelAsValue ? COUNTRIES[countryCode] : countryCode,
        label: COUNTRIES[countryCode]
    }));
    if (withAll)
        items.splice(0, 0, {
            label: t(`${languageKey}.all`, 'Alle') + ' ' + t(`${languageKey}.countries`, 'landen'),
            value: ''
        });

    return (
        <BluemateSelect
            value={value ?? 'NL'}
            onChange={onChange}
            items={items}
            disabled={disabled}
            className={className}
            label={label ?? `${t(`${languageKey}.country`, 'Land')} *`}
            itemsPosition={itemsPosition}
            itemsHeight={itemsHeight ?? '20rem'}
            labelClassName={labelClassName}
            active={active}
            itemContainerClassName={itemContainerClassName}
            canOpen={canOpen}
            shape={shape}
            uniformFont={uniformFont}
        />
    );
}
